<template>
  <section class="p-8 m-3 flex-wrap flex justify-between">
    <form class="w-full max-w-md m-3">
      <div class="field">
        <label
          for="form-title"
          v-text="'Title'"
        />
        <input
          id="form-title"
          v-model="form.title"
          name="form-title"
          type="text"
          placeholder="Event Title"
        >
        <div
          v-if="form.errors.has('title')"
          class="field__meta"
        >
          <span
            class="field__meta__help"
            v-text="form.errors.get('title')"
          />
        </div>
      </div>
      <div class="field">
        <label
          for="form-location"
          v-text="'Location'"
        />
        <input
          id="form-location"
          ref="location"
          v-model="form.location"
          name="form-location"
          type="text"
          placeholder="Event location"
        >
        <div
          v-if="form.errors.has('location')"
          class="field__meta"
        >
          <span
            class="field__meta__help"
            v-text="form.errors.get('location')"
          />
        </div>
      </div>
      <div class="field">
        <label
          for="form-details"
          v-text="'Details'"
        />
        <textarea
          id="form-details"
          v-model="form.details"
          name="form-details"
          placeholder="Event Details"
          rows="8"
        />
        <div
          v-if="form.errors.has('details')"
          class="field__meta"
        >
          <span
            class="field__meta__help"
            v-text="form.errors.get('details')"
          />
        </div>
      </div>
      <div class="field-list">
        <div class="field w-1/2">
          <label v-text="'Start'" />
          <BaseDatePicker
            v-model="form.start_date"
            placeholder="Start date"
            :config="datePickerConfig"
          />
          <div
            v-if="form.errors.has('start_date')"
            class="field__meta"
          >
            <span
              class="field__meta__help"
              v-text="form.errors.get('start_date')"
            />
          </div>
        </div>
        <div class="field w-1/2">
          <label v-text="'End'" />
          <BaseDatePicker
            v-model="form.end_date"
            placeholder="End date"
            :config="datePickerConfig"
          />
          <div
            v-if="form.errors.has('end_date')"
            class="field__meta"
          >
            <span
              class="field__meta__help"
              v-text="form.errors.get('end_date')"
            />
          </div>
        </div>
      </div>
      <div class="field mb-8">
        <input
          id="form-all-day"
          v-model="form.all_day"
          name="form-all-day"
          type="checkbox"
        >
        <label
          for="form-all-day"
          v-text="'All Day'"
        />
        <div
          v-if="form.errors.has('all_day')"
          class="field__meta"
        >
          <span
            class="field__meta__help"
            v-text="form.errors.get('all_day')"
          />
        </div>
      </div>
      <div class="field">
        <label
          for="form-timezone"
          v-text="'Timezone'"
        />
        <div class="select-container">
          <select
            id="form-timezone"
            v-model="form.timezone"
            name="form-timezone"
          >
            <option
              v-for="timezone in timezones"
              :key="`timzeone-${timezone.id}`"
              :value="timezone.id"
              v-text="timezone.title"
            />
          </select>
        </div>
        <div
          v-if="form.errors.has('timezone')"
          class="field__meta"
        >
          <span
            class="field__meta__help"
            v-text="form.errors.get('timezone')"
          />
        </div>
      </div>
      <div class="field">
        <label for="form-user-group">
          User Groups
          <span
            v-if="groups.length === 0"
            class="text-red text-xs"
            v-text="'Please visit your groups page and select which should have calendar visibility'"
          />
        </label>
        <div class="select-container">
          <select
            id="form-user-group"
            v-model="form.membership_group_id"
            name="form-user-group"
          >
            <option
              v-for="(group,index) in groups"
              :key="`question-${index}`"
              :value="group.uuid"
              v-text="group.title"
            />
          </select>
        </div>
        <div
          v-if="form.errors.has('membership_group_id')"
          class="field__meta"
        >
          <span
            class="field__meta__help"
            v-text="form.errors.get('membership_group_id')"
          />
        </div>
      </div>
      <div class="field">
        <label
          for="form-web-link"
          v-text="'Web Link'"
        />
        <input
          id="form-web-link"
          v-model="form.web_link"
          name="form-web-link"
          type="text"
          placeholder="http://www.eventwebsite.com"
        >
        <div
          v-if="form.errors.has('web_link')"
          class="field__meta"
        >
          <span
            class="field__meta__help"
            v-text="form.errors.get('web_link')"
          />
        </div>
      </div>
      <div class="field">
        <label v-text="'Event Display Image'" />
        <a
          v-if="form.image"
          href="#"
          class="text-sm"
          @click.prevent="form.imagesToRemove.push(form.image.uuid); form.image = ''"
          v-text="'Remove existing?'"
        />
        <div v-if="form.image">
          <img
            :src="form.image.url"
            class="rounded-lg my-2"
          >
        </div>
        <BaseDropzone
          v-show="!form.image"
          id="dropzone"
          ref="dropzone"
          inline-previews
          :options="{
            url: this.$urlGenerator.active(),
            maxFiles: 1,
            paramName: 'file',
            dictDefaultMessage: 'Drop file here to upload',
            acceptedFiles: 'image/*',
            autoProcessQueue: false,
          }"
          @dropzone-queue-complete="redirect"
        />
      </div>
      <div class="field mb-8">
        <input
          id="form-send-notification"
          v-model="form.send_notification"
          name="form-send-notification"
          type="checkbox"
        >
        <label
          for="form-send-notification"
          v-text="'Send Notification'"
        />
        <div
          v-if="form.errors.has('send_notification')"
          class="field__meta"
        >
          <span
            class="field__meta__help"
            v-text="form.errors.get('send_notification')"
          />
        </div>
      </div>
      <button
        type="button"
        class="btn"
        @click.prevent="submit"
        v-text="'Save'"
      />
    </form>
  </section>
</template>

<script type="text/babel">
import BaseDatePicker from 'Base/Resources/assets/js/components/BaseDatePicker'
import Form from 'Base/Resources/assets/js/MUMForm'
import UploadDocument from 'Base/Resources/assets/js/mixins/UploadDocumentMixin'
import moment from 'moment'

export default {
  name: 'CalendarEventForm',
  components: {
    BaseDatePicker,
  },
  mixins: [UploadDocument],
  props: {
    groups: {
      type: Array,
      required: false,
      default: () => [],
    },
    timezones: {
      type: Array,
      required: true,
    },
    calendarEventUuid: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      form: new Form({
        uuid: null,
        title: null,
        location: null,
        details: null,
        start_date: this.initStartDate(),
        end_date: null,
        all_day: false,
        send_notification: false,
        web_link: 'https://www.',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        membership_group_id: null,
        filesInQueue: false,
        uploadingDocuments: false,
        currentUploadCount: false,
        totalUploadCount: false,
        existingDocuments: false,
        image: null,
        imagesToRemove: [],
      }),
      isLoading: false,
      isUploading: false,
      errors: {},
      datePickerBaseConfig : {
        minDate: 'today',
      },
    }
  },
  computed: {
    exists() {
      return !!this.form.uuid
    },
    datePickerConfig() {
      const config = {
        minDate: this.datePickerBaseConfig.minDate,
      }

      if (this.form.all_day) {
        config['altFormat'] = this.$date.flatPickerDateFormat()
      }

      return config
    },
  },
  watch: {
    'form.start_date'(newVal, oldVal) {
      // We don't modify end date when editing event
      if (this.calendarEventUuid) {
        return
      }

      this.$set(this.form, 'end_date', moment(newVal).add(1, 'hours').format('YYYY-MM-DD HH:mm:ss'))
    },
  },
  async created() {
    if (this.calendarEventUuid) {
      await this.$nextTick()

      this.form.uuid = this.calendarEventUuid

      this.isLoading = true

      this.$api
        .active()
        .get(`calendar/events/${this.calendarEventUuid}`)
        .then(({ data }) => {
          this.$set(this.form, 'title', data.attributes.title)
          this.$set(this.form, 'location', data.attributes.location)
          this.$set(this.form, 'details', data.attributes.details)
          this.$set(this.form, 'start_date', data.attributes.start_date)
          this.$set(this.form, 'end_date', data.attributes.end_date)
          this.$set(this.form, 'all_day', data.attributes.all_day)
          this.$set(this.form, 'send_notification', data.attributes.send_notification)
          this.$set(this.form, 'web_link', data.attributes.web_link)
          this.$set(this.form, 'timezone', data.attributes.timezone)
          this.$set(this.form, 'membership_group_id', data.attributes.membership_group_id)
          this.$set(this.form, 'image', data.attributes.image)

          // If start date is in future, we will disable from today
          // Otherwise, we disable from start_date
          if (moment().isAfter(data.attributes.start_date)) {
            this.$set(this.datePickerBaseConfig, 'minDate', data.attributes.start_date)
          }

          this.isLoading = false
        })
    }
  },
  mounted() {
    const autoComplete = new google.maps.places.Autocomplete(this.$refs.location)

    google.maps.event.addListener(autoComplete, 'place_changed', () => {
      const place = autoComplete.getPlace()

      if (place && place.formatted_address) {
        this.$set(this.form, 'location', place.formatted_address)
      }
    })
  },
  methods: {
    initStartDate() {
      const startDate = (new URLSearchParams(window.location.search)).get('start_date')

      if (startDate && moment(startDate).isAfter(moment())) {
        return moment(startDate).format('YYYY-MM-DD HH:mm:ss')
      }

      return moment().format('YYYY-MM-DD HH:mm:ss')
    },
    submit() {
      this.save().then(() => {
        this.uploadOrDeleteEventImage()
      })
    },
    save() {
      this.form.startProcessing()

      return new Promise((resolve, reject) => {
        this.isLoading = true

        let action = 'post'
        let url = 'calendar/events'

        if (this.exists) {
          action = 'put'
          url = `calendar/events/${this.form.uuid}`
        }

        this.$api
          .active()
          [action](url, this.buildPayload())
          .then((response) => {
            if (!this.exists) {
              this.form.uuid = response.data.uuid
            }

            this.isLoading = false

            resolve(response)
          })
          .catch((errors) => {
            this.form.setErrors(errors.response.data)

            this.isLoading = false

            reject()
          })
      })
    },
    uploadOrDeleteEventImage() {
      if (this.form.imagesToRemove.length) {
        this.form.imagesToRemove.forEach((documentUuid) => {
          this.$api.active().delete(`documents/${documentUuid}`)
        })
      }

      this.upload(
        this.$urlGenerator.active(
          `api/v1/calendar/events/${this.form.uuid}/upload`
        )
      )

      if (!this.form.uploadingDocuments) {
        this.redirect()
      }
    },
    redirect() {
      window.location = this.$urlGenerator.active(
        `calendar/events/${this.form.uuid}`
      )
    },
    buildPayload() {
      const payload = {}

      payload.title = this.form.title
      payload.location = this.form.location
      payload.details = this.form.details
      payload.start_date = this.form.start_date
      payload.end_date = this.form.end_date
      payload.all_day = this.form.all_day
      payload.send_notification = this.form.send_notification
      payload.web_link = this.form.web_link
      payload.timezone = this.form.timezone
      payload.membership_group_id = this.form.membership_group_id

      return payload
    },
  },
}
</script>
